/**
 * LUXFileViewer_R: React 19 호환 파일 뷰어 컴포넌트
 *
 * luna-rocket LUXFileViewer_R을 wehago-conference2 프로젝트에 맞게 포팅.
 * 원본 CSS 클래스명(LS_viewer_new, viewer_wrap 등)과 마크업 구조를 그대로 유지하여
 * CDN selene.css/lux.css 스타일이 정상 적용됩니다.
 *
 * 변경사항:
 * - Class Component → Functional Component (React 19 호환)
 * - String refs → useRef
 * - react-event-listener → useEffect + addEventListener
 * - regeneratorRuntime → native async/await
 * - LUXVirtualBox → native overflow scroll (회의자료 규모에 최적화)
 * - LUXCircularProgress → 내장 CSS spinner
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { getFileIconUrl } from '@/utils/fileIcon';

// ── 타입 ────────────────────────────────────────────────────────────────────

export interface FileViewerFileInfo {
  name: string;
  type: string;
  size?: string;
  mimeType: 'IMAGE' | 'OFFICE' | 'ETC';
  createDate?: string;
  uploadDate?: string;
  lastModifyDate?: string;
  owner?: string;
  lastModifyUser?: string;
}

export interface FileViewerStorageInfo {
  FileName?: string;
  BucketType?: string;
  cno?: string;
  tokenID?: string;
  wehago_s?: string;
  [key: string]: unknown;
}

export interface LUXFileViewerProps {
  fileInfo: FileViewerFileInfo;
  storageInfo: FileViewerStorageInfo;
  authority?: string;
  onClose?: () => void;
  onAction?: (type: string, index?: number, ...args: unknown[]) => void;
  /** 데이터 로드 함수 오버라이드. Promise<{ json(): Promise<any> }> 반환 */
  getData?: () => Promise<{ json(): Promise<GetDataResponse> }>;
  useOpenButton?: boolean;
  useRenewalStyle?: boolean;
  dimmedStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  isONEAIEnabled?: boolean;
  /** CDN static 경로 (파일 아이콘). 기본: https://static.wehago.com */
  staticPath?: string;
}

// getData 응답 타입
interface GetDataResponse {
  resultCode: string;
  resultList?: Array<
    | { resources: string[]; isFullPreview?: boolean }  // OFFICE
    | string                                             // IMAGE (URL)
  >;
  resultData?: { resultCode: string; resultList: string[] };
}

interface PageData {
  index: number;
  url: string;       // resource URL or 'dummy'
  loaded: boolean;
}

interface ImageSize {
  width: number;
  height: number;
}

// ── 상수 ────────────────────────────────────────────────────────────────────

const DEFAULT_STATIC_PATH = 'https://static.wehago.com';
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.1;
const HEADER_HEIGHT = 52;
const SIDEBAR_WIDTH = 226;

// ── CSS Spinner (LUXCircularProgress 대체) ──────────────────────────────────

const SPINNER_KEYFRAMES = `
@keyframes lux-fileviewer-spin {
  to { transform: rotate(360deg); }
}`;

let spinnerStyleInjected = false;
function injectSpinnerStyle() {
  if (spinnerStyleInjected) return;
  const style = document.createElement('style');
  style.textContent = SPINNER_KEYFRAMES;
  document.head.appendChild(style);
  spinnerStyleInjected = true;
}

const Spinner: React.FC<{
  size?: number;
  text?: string;
  style?: React.CSSProperties;
}> = ({ size = 200, text = 'Loading...', style }) => {
  useEffect(() => { injectSpinnerStyle(); }, []);
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      width: size, height: size, ...style,
    }}>
      <div style={{
        width: 48, height: 48,
        border: '4px solid rgba(255,255,255,0.3)',
        borderTopColor: '#fff', borderRadius: '50%',
        animation: 'lux-fileviewer-spin 0.8s linear infinite',
      }} />
      {text && <span style={{ color: '#fff', fontSize: 14, marginTop: 12 }}>{text}</span>}
    </div>
  );
};

// ── 유틸리티 ────────────────────────────────────────────────────────────────

function clampZoom(ratio: number): number {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(ratio * 10) / 10));
}

function trimOwner(s: string): string {
  return s.charAt(s.length - 1) === '@' ? s.substring(0, s.length - 1) : s;
}

// ── 메인 컴포넌트 ───────────────────────────────────────────────────────────

const LUXFileViewer_R: React.FC<LUXFileViewerProps> = ({
  fileInfo,
  storageInfo,
  authority = 'read',
  onClose,
  onAction,
  getData,
  useOpenButton = true,
  useRenewalStyle = false,
  dimmedStyle,
  contentStyle,
  staticPath = DEFAULT_STATIC_PATH,
}) => {
  // ── Refs ──────────────────────────────────────────────────────────────────
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const sidebarScrollRef = useRef<HTMLDivElement>(null);
  const fileInfoBtnRef = useRef<HTMLButtonElement>(null);
  const fileInfoTabRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);
  const loadStartedRef = useRef(false);

  // ── State ─────────────────────────────────────────────────────────────────
  const [progressVisible, setProgressVisible] = useState(false);
  const [disableImg, setDisableImg] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [pageLength, setPageLength] = useState(0);
  const [pages, setPages] = useState<PageData[]>([]);
  const [imageSize, setImageSize] = useState<ImageSize>({ width: 0, height: 0 });
  const [bodySize, setBodySize] = useState<ImageSize>({ width: 0, height: 0 });
  const [zoomRatio, setZoomRatio] = useState(0.7);
  const [basicZoomRatio, setBasicZoomRatio] = useState(0.7);
  const [fitWidth, setFitWidth] = useState(false);
  const [fitPage, setFitPage] = useState(false);
  const [actualSize, setActualSize] = useState(false);
  const [fitSize, setFitSize] = useState(false);
  const [headerState, setHeaderState] = useState('default');
  const [sizeData, setSizeData] = useState<ImageSize[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [isZoomVisible, setIsZoomVisible] = useState(false);
  const [isFileInfoVisible, setIsFileInfoVisible] = useState(false);
  const [pageRotation, setPageRotation] = useState<number[]>([0, 0]);
  const [fileInfoPos, setFileInfoPos] = useState({ top: 0, left: 0 });
  const [sideBarOpen, setSideBarOpen] = useState(true);

  const isImageFile = fileInfo.mimeType === 'IMAGE';
  // 사이드바 표시 여부: OFFICE 파일이면서 여러 페이지
  const hasSideBar = fileInfo.mimeType === 'OFFICE' && pageLength > 1;

  // ── 마운트 / 언마운트 ─────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Body 사이즈 계산 ──────────────────────────────────────────────────────
  const updateBodySize = useCallback(() => {
    if (rootRef.current) {
      setBodySize({
        width: rootRef.current.clientWidth,
        height: rootRef.current.clientHeight - HEADER_HEIGHT,
      });
    }
  }, []);

  useEffect(() => {
    updateBodySize();
    window.addEventListener('resize', updateBodySize);
    return () => window.removeEventListener('resize', updateBodySize);
  }, [updateBodySize]);

  // ── 줌 비율 계산 ──────────────────────────────────────────────────────────
  const calculateZoom = useCallback((imgW: number, imgH: number, bSize: ImageSize) => {
    const isOffice = fileInfo.mimeType === 'OFFICE' || fileInfo.mimeType === 'ETC';
    let zoom = 0.7;

    if (imgW < bSize.width && imgH < bSize.height) {
      zoom = 1;
    } else if (!isOffice && imgW < imgH) {
      const hRatio = bSize.height / imgH;
      const w = imgW * hRatio;
      zoom = w / bSize.width;
    } else if (fileInfo.mimeType === 'IMAGE' && imgH * zoom > bSize.height) {
      while (zoom > 0.2) {
        zoom -= 0.1;
        if (imgH * (bSize.width / imgW) * zoom < bSize.height - 60) break;
      }
    }

    let newFitPage = false;
    let newFitWidth = false;
    let newHeaderState = 'default';

    if (isOffice && imgW < imgH) {
      const hRatio = bSize.height / imgH;
      const w = imgW * hRatio;
      zoom = w / bSize.width - 0.003;
      newFitPage = true;
      newHeaderState = 'fitPage';
    } else if (isOffice && imgW > imgH) {
      zoom = 1;
      newFitWidth = true;
      newHeaderState = 'fitWidth';
    }

    if (!mountedRef.current) return;

    setImageSize({ width: imgW, height: imgH });
    setZoomRatio(zoom);
    setBasicZoomRatio(zoom);
    setFitSize(imgW < bSize.width && imgH < bSize.height);
    setFitPage(newFitPage);
    setFitWidth(newFitWidth);
    setHeaderState(newHeaderState);
  }, [fileInfo.mimeType]);

  // ── 페이지 요소 크기 계산 (원본 Body.getElementSize 포팅) ─────────────────
  const getElementSize = useCallback((index?: number): { width: number; height: number } => {
    const sidebarW = hasSideBar && sideBarOpen ? SIDEBAR_WIDTH : 0;
    const bWidth = (bodySize.width || 800) - sidebarW - 15; // scrollbar 여유

    let imgW: number, imgH: number;
    if (index !== undefined && sizeData[index]) {
      imgW = sizeData[index].width || 1;
      imgH = sizeData[index].height || 1;
    } else {
      imgW = imageSize.width;
      imgH = imageSize.height;
    }

    let w: number, h: number;

    if (actualSize) {
      if (!imgW || !imgH) {
        w = bWidth * zoomRatio;
        h = ((bodyRef.current?.clientHeight ?? bodySize.height) || 500) * zoomRatio;
      } else {
        w = imgW * zoomRatio;
        h = imgH * zoomRatio;
      }
    } else if (
      fitSize &&
      ((headerState === 'zoomIn' || headerState === 'zoomOut') && !fitWidth && !fitPage ||
        headerState === 'default')
    ) {
      w = imgW * zoomRatio;
      h = imgH * zoomRatio;
    } else if (!imgW || !imgH) {
      w = bWidth * zoomRatio;
      h = (bodyRef.current?.clientHeight ?? bodySize.height) || 500;
    } else {
      w = bWidth * zoomRatio;
      h = imgH * (w / imgW);
    }

    return { width: w, height: h };
  }, [bodySize, sizeData, imageSize, actualSize, zoomRatio, fitSize, headerState, fitWidth, fitPage, hasSideBar, sideBarOpen]);

  // ── 데이터 로드 ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (loadStartedRef.current || !getData) return;
    loadStartedRef.current = true;

    if (isImageFile) {
      loadImageData();
    } else {
      loadOfficeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadOfficeData() {
    if (!getData || !mountedRef.current) return;

    setProgressVisible(true);

    try {
      const response = await getData();
      const result = await response.json();
      if (!mountedRef.current) return;

      if (result.resultCode === '0000') {
        const firstEntry = result.resultList?.[0];
        const resources = (firstEntry && typeof firstEntry === 'object' && 'resources' in firstEntry)
          ? (firstEntry as { resources: string[]; isFullPreview?: boolean }).resources
          : null;

        if (resources && resources.length > 0) {
          const pageArr: PageData[] = resources.map((url, i) => ({
            index: i, url, loaded: false,
          }));

          const bSize = rootRef.current
            ? { width: rootRef.current.clientWidth, height: rootRef.current.clientHeight - HEADER_HEIGHT }
            : bodySize;

          setPages(pageArr);
          setPageLength(resources.length);
          setPageRotation(new Array(resources.length + 1).fill(0));
          setBodySize(bSize);
          return;
        }
      }

      if (result.resultCode === 'E2021') {
        setProgressVisible(false);
        setDisableImg(true);
        setErrorMsg('문서 변환에 실패하였습니다.\n잠시 후 다시 시도해 주세요.');
      } else {
        setProgressVisible(false);
        setDisableImg(true);
        setErrorMsg('문서를 미리볼 수 없습니다.');
      }
    } catch {
      if (mountedRef.current) {
        setProgressVisible(false);
        setDisableImg(true);
        setErrorMsg('문서를 미리볼 수 없습니다.');
      }
    }
  }

  async function loadImageData() {
    if (!getData || !mountedRef.current) return;

    setProgressVisible(true);

    try {
      const response = await getData();
      const result = await response.json();
      if (!mountedRef.current) return;

      const resolved = result.resultData ?? result;
      if (resolved.resultCode !== '0000') {
        setProgressVisible(false);
        setDisableImg(true);
        setErrorMsg('이미지를 미리볼 수 없습니다.');
        return;
      }

      const imgUrl = resolved.resultList?.[0];
      if (!imgUrl || typeof imgUrl !== 'string') {
        setProgressVisible(false);
        setDisableImg(true);
        setErrorMsg('이미지를 미리볼 수 없습니다.');
        return;
      }

      const bSize = rootRef.current
        ? { width: rootRef.current.clientWidth, height: rootRef.current.clientHeight - HEADER_HEIGHT }
        : bodySize;

      setPages([{ index: 0, url: imgUrl, loaded: false }]);
      setPageLength(1);
      setPageRotation([0, 0]);
      setBodySize(bSize);

      const probeImg = new Image();
      probeImg.src = imgUrl;
      probeImg.addEventListener('load', function (this: HTMLImageElement) {
        if (!mountedRef.current) return;
        calculateZoom(this.naturalWidth, this.naturalHeight, bSize);
        setPages([{ index: 0, url: imgUrl, loaded: true }]);
        setProgressVisible(false);
      });
      probeImg.addEventListener('error', () => {
        if (!mountedRef.current) return;
        setProgressVisible(false);
        setDisableImg(true);
        setErrorMsg('이미지에 문제가 발생하여 미리볼 수 없습니다.');
      });
    } catch {
      if (mountedRef.current) {
        setProgressVisible(false);
        setDisableImg(true);
        setErrorMsg('이미지를 미리볼 수 없습니다.');
      }
    }
  }

  // ── OFFICE 페이지 이미지 onLoad 핸들러 ────────────────────────────────────
  const handlePageImageLoad = useCallback((
    e: React.SyntheticEvent<HTMLImageElement>,
    index: number,
  ) => {
    if (!mountedRef.current) return;
    const img = e.currentTarget;
    const size: ImageSize = { width: img.naturalWidth, height: img.naturalHeight };

    if (index === 0) {
      const bSize = rootRef.current
        ? { width: rootRef.current.clientWidth, height: rootRef.current.clientHeight - HEADER_HEIGHT }
        : bodySize;
      calculateZoom(img.naturalWidth, img.naturalHeight, bSize);
      setProgressVisible(false);
    }

    setPages(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], loaded: true };
      return updated;
    });

    setSizeData(prev => {
      const updated = [...prev];
      updated[index] = size;
      return updated;
    });
  }, [bodySize, calculateZoom]);

  const handlePageImageError = useCallback((index: number) => {
    if (!mountedRef.current) return;
    setPages(prev => {
      const updated = [...prev];
      updated[index] = { index, url: 'error', loaded: true };
      return updated;
    });
    if (index === 0) {
      setProgressVisible(false);
    }
  }, []);

  // ── 키보드 이벤트 ─────────────────────────────────────────────────────────
  // capture 단계에서 처리하여 SettingsDialog 등 부모의 bubble keydown 핸들러보다
  // 먼저 실행되도록 하고, stopImmediatePropagation으로 이후 핸들러를 차단한다.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose]);

  // ── Zoom indicator 자동 숨김 ──────────────────────────────────────────────
  useEffect(() => {
    if (!isZoomVisible) return;
    const timer = setTimeout(() => setIsZoomVisible(false), 1500);
    return () => clearTimeout(timer);
  }, [isZoomVisible, zoomRatio]);

  // ── File info popup 위치 업데이트 ─────────────────────────────────────────
  useEffect(() => {
    if (isFileInfoVisible && fileInfoBtnRef.current) {
      const rect = fileInfoBtnRef.current.getBoundingClientRect();
      const popupWidth = fileInfoTabRef.current?.getBoundingClientRect().width ?? 244;
      setFileInfoPos({
        top: rect.bottom + 15,
        left: rect.left - (popupWidth / 2 - 7),
      });
    }
  }, [isFileInfoVisible]);

  // ── 사이드바 클릭으로 페이지 이동 ─────────────────────────────────────────
  const handleSideBarClick = useCallback((targetIndex: number) => {
    if (!bodyRef.current) return;
    setPageIndex(targetIndex + 1);

    // 해당 페이지의 view_item 요소를 찾아 직접 스크롤
    const items = bodyRef.current.querySelectorAll('.view_item');
    if (items[targetIndex]) {
      items[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // ── 액션 핸들러 ───────────────────────────────────────────────────────────
  const handleAction = useCallback((type: string) => {
    switch (type) {
      case 'close':
        onClose?.();
        break;

      case 'sideBar':
        setSideBarOpen(prev => !prev);
        break;

      case 'zoomIn': {
        setZoomRatio(prev => {
          if (prev >= ZOOM_MAX) return prev;
          return clampZoom(prev + ZOOM_STEP);
        });
        setHeaderState('zoomIn');
        setIsZoomVisible(true);
        break;
      }

      case 'zoomOut': {
        setZoomRatio(prev => {
          const next = clampZoom(prev - ZOOM_STEP);
          if (next <= ZOOM_MIN) return prev;
          return next;
        });
        setHeaderState('zoomOut');
        setIsZoomVisible(true);
        break;
      }

      case 'fitWidth': {
        setFitWidth(prev => {
          if (prev) {
            setZoomRatio(basicZoomRatio);
            setHeaderState('default');
            return false;
          }
          setZoomRatio(1);
          setFitPage(false);
          setActualSize(false);
          setHeaderState('fitWidth');
          return true;
        });
        break;
      }

      case 'fitPage': {
        setFitPage(prev => {
          if (prev) {
            setZoomRatio(basicZoomRatio);
            setHeaderState('default');
            return false;
          }
          if (imageSize.width && imageSize.height && bodySize.height) {
            const hRatio = bodySize.height / imageSize.height;
            const w = imageSize.width * hRatio;
            const ratio = w / bodySize.width - 0.003;
            setZoomRatio(ratio);
          }
          setFitWidth(false);
          setActualSize(false);
          setHeaderState('fitPage');
          return true;
        });
        break;
      }

      case 'actualSize': {
        setActualSize(prev => {
          if (prev) {
            setZoomRatio(basicZoomRatio);
            setHeaderState('default');
            return false;
          }
          setFitPage(false);
          setFitWidth(false);
          setZoomRatio(1);
          setHeaderState('actualSize');
          return true;
        });
        break;
      }

      case 'rotation': {
        setPageRotation(prev => {
          const arr = [...prev];
          arr[pageIndex] = (arr[pageIndex] + 90) % 360;
          return arr;
        });
        break;
      }

      case 'downLoad':
        onAction?.('downLoad');
        break;

      case 'fileInfo':
        setIsFileInfoVisible(prev => !prev);
        break;
    }
  }, [onClose, onAction, basicZoomRatio, imageSize, bodySize, pageIndex]);

  // ── dimmed 클릭으로 닫기 ──────────────────────────────────────────────────
  const handleClickDimmed = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (typeof target.className === 'string' && target.className.includes('viewer_wrap')) {
      onClose?.();
    }
  }, [onClose]);

  // ── contents_page v2 영역 클릭으로 닫기 (singleimg 제외) ─────────────────
  const handleContentsPageClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.singleimg')) return;
    onClose?.();
  }, [onClose]);

  // ── 스크롤 페이지 인덱스 추적 ─────────────────────────────────────────────
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (pageLength <= 1) return;
    const el = e.currentTarget;
    const elemSize = getElementSize(0);
    const pageH = elemSize.height + 10;
    if (pageH > 0) {
      const idx = Math.floor(el.scrollTop / pageH) + 1;
      setPageIndex(Math.min(Math.max(idx, 1), pageLength));
    }
  }, [pageLength, getElementSize]);

  // ── 렌더링: 헤더 컨트롤 버튼 목록 ────────────────────────────────────────
  const controlButtons = useMemo(() => {
    const buttons = [
      { label: 'fitWidth', icon: 'icon_wide', check: fitWidth, title: '너비 맞춤' },
      { label: 'fitPage', icon: 'icon_height', check: fitPage, title: '높이 맞춤' },
      { label: 'actualSize', icon: 'icon_real', check: actualSize, title: '실제 사이즈' },
      { label: 'zoomIn', icon: 'icon_zoomin', title: '확대' },
      { label: 'zoomOut', icon: 'icon_zoomout', title: '축소' },
      { label: 'rotation', icon: 'icon_rotate', title: '회전' },
      { label: 'downLoad', icon: 'icon_download', title: '다운로드' },
    ];

    return buttons.map((btn, i) => {
      if (btn.label === 'fitPage' && imageSize.width > imageSize.height * 2.5) return null;
      if (btn.label === 'rotation' && pageLength !== 1) return null;

      let className = `LS_btn Image ${btn.icon}`;
      if (btn.check) className += ' on';

      return (
        <button
          key={i}
          type="button"
          className={className}
          title={btn.title}
          onClick={() => handleAction(btn.label)}
        >
          <span className="sp_selene">{btn.title}</span>
        </button>
      );
    });
  }, [fitWidth, fitPage, actualSize, imageSize, pageLength, handleAction]);

  // ── 렌더링: 단일 페이지 (IMAGE or 1장 OFFICE) ────────────────────────────
  const renderSinglePage = () => {
    const page = pages[0];
    if (!page) return null;

    const elemSize = getElementSize(0);
    const rotation = pageRotation[1] || 0;
    const isRotated = rotation === 90 || rotation === 270;
    const margin = isRotated
      ? `${Math.abs(elemSize.width - elemSize.height) / 2 * (elemSize.height > elemSize.width ? -1 : 1)}px auto`
      : '10px auto 0';

    const hasUrl = page.url && page.url !== 'dummy' && page.url !== 'error';

    return (
      <div
        className="view_item"
        style={{
          height: elemSize.height ? elemSize.height - 10 : undefined,
          width: elemSize.width,
          margin,
          transform: `rotate(${rotation}deg)`,
        }}
      >
        {isImageFile ? (
          <div
            className="singleimg"
            style={{
              backgroundImage: hasUrl ? `url(${page.url})` : undefined,
              width: elemSize.width,
              height: elemSize.height ? elemSize.height - 10 : undefined,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          />
        ) : (
          <img
            src={hasUrl ? page.url : undefined}
            onError={() => {
              if (mountedRef.current) {
                setDisableImg(true);
                setErrorMsg('문서를 미리볼 수 없습니다.');
              }
            }}
            onLoad={() => {
              if (mountedRef.current) setProgressVisible(false);
            }}
            style={{
              width: elemSize.width,
              height: elemSize.height ? elemSize.height - 10 : undefined,
              objectFit: 'contain',
            }}
            alt=""
          />
        )}
      </div>
    );
  };

  // ── 렌더링: 여러 페이지 (OFFICE 문서) ────────────────────────────────────
  const renderMultiPages = () =>
    pages.map((page, i) => {
      const pgSize = getElementSize(i);
      const hasUrl = page.url && page.url !== 'dummy' && page.url !== 'error';

      return (
        <div
          key={page.index}
          className="view_item"
          style={{
            height: pgSize.height,
            width: pgSize.width,
            margin: '10px auto',
          }}
        >
          {hasUrl ? (
            <img
              src={page.url}
              onLoad={(e) => handlePageImageLoad(e, i)}
              onError={() => handlePageImageError(i)}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              alt={`page ${i + 1}`}
            />
          ) : page.url === 'error' ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '100%', height: '100%', color: '#999',
            }}>
              페이지를 불러올 수 없습니다.
            </div>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '100%', height: '100%',
            }}>
              <Spinner size={60} text="" style={{ position: 'static' }} />
            </div>
          )}
        </div>
      );
    });

  // ── 렌더링: 사이드바 (OFFICE 다중 페이지용 페이지 네비게이터) ──────────────
  const renderSideBar = () => {
    if (!hasSideBar) return null;

    const sideBarStyle: React.CSSProperties = {
      width: `${SIDEBAR_WIDTH}px`,
      position: 'fixed',
      top: '52px',
      left: '0px',
      bottom: '0px',
      display: sideBarOpen ? undefined : 'none',
      background: sideBarOpen ? 'rgba(0,0,0,.35)' : undefined,
      overflowY: 'auto',
      overflowX: 'hidden',
      zIndex: 10,
    };

    const thumbHeight = imageSize.width > imageSize.height ? 109 : 205;

    return (
      <div ref={sidebarScrollRef} style={sideBarStyle}>
        <ul style={{ listStyle: 'none', margin: 0, padding: '5px 0' }}>
          {pages.map((page, i) => {
            const isActive = pageIndex === i + 1;
            const hasUrl = page.url && page.url !== 'dummy' && page.url !== 'error';

            return (
              <li key={i}>
                <a
                  href="#"
                  className={isActive ? 'contents_page on' : 'contents_page'}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSideBarClick(i);
                  }}
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '5px 10px',
                    cursor: 'pointer',
                  }}
                >
                  {hasUrl ? (
                    <img
                      src={page.url}
                      style={{ width: '100%', maxHeight: `${thumbHeight}px`, objectFit: 'contain' }}
                      alt={`page ${i + 1}`}
                    />
                  ) : (
                    <div style={{
                      width: '145px',
                      height: `${thumbHeight}px`,
                      background: 'gray',
                      margin: '0 auto',
                    }} />
                  )}
                </a>
                <span
                  className="pagenum"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    width: '146px',
                    margin: '0 auto',
                    color: isActive ? '#fff' : '#aaa',
                    fontSize: '12px',
                    paddingBottom: '5px',
                  }}
                >
                  {i + 1}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  // ── 렌더링: Body ──────────────────────────────────────────────────────────
  const renderBody = () => {
    if (pageLength === 0) return null;

    return (
      <div ref={bodyRef} className="viewer_contents" onScroll={handleScroll}>
        <div
          className="tbwrap"
        >
          <div
            className="contents_page v2"
            onClick={handleContentsPageClick}
          >
            {pageLength === 1 ? renderSinglePage() : renderMultiPages()}
          </div>
        </div>

        {/* 페이지/줌 인디케이터 */}
        <div className="viewer_utils">
          {pageLength > 1 && !isNaN(pageIndex) && (
            <div className="page_box" onClick={e => e.stopPropagation()}>
              <p><em>{pageIndex}</em>/{pageLength} 쪽</p>
            </div>
          )}
          {isZoomVisible && (
            <div className="page_box zoom">
              <p>{Math.round(zoomRatio * 100)}%</p>
            </div>
          )}
        </div>

        {/* 사이드바: non-renewal 스타일에서는 Body 안에 렌더 (원본 구조 동일) */}
        {renderSideBar()}
      </div>
    );
  };

  // ── 렌더링: File Info Popup ───────────────────────────────────────────────
  const renderFileInfoPopup = () => (
    <div
      ref={fileInfoTabRef}
      className="viewer__docInfo"
      style={{
        top: fileInfoPos.top,
        left: fileInfoPos.left,
        visibility: isFileInfoVisible ? 'visible' : 'hidden',
      }}
    >
      <p className="viewer__docInfoTitle">일반 정보</p>
      <dl className="viewer__docInfoList">
        <div className="viewer__docInfoItem">
          <dt>파일명</dt>
          <dd>{fileInfo.name}</dd>
        </div>
        {fileInfo.size && (
          <div className="viewer__docInfoItem">
            <dt>크기</dt>
            <dd>{fileInfo.size}</dd>
          </div>
        )}
        {fileInfo.type && (
          <div className="viewer__docInfoItem">
            <dt>종류</dt>
            <dd>{fileInfo.type.toUpperCase()}</dd>
          </div>
        )}
        {fileInfo.createDate && (
          <div className="viewer__docInfoItem">
            <dt>작성 날짜</dt>
            <dd>{fileInfo.createDate}</dd>
          </div>
        )}
        {fileInfo.uploadDate && (
          <div className="viewer__docInfoItem">
            <dt>업로드 날짜</dt>
            <dd>{fileInfo.uploadDate}</dd>
          </div>
        )}
        {fileInfo.lastModifyDate && (
          <div className="viewer__docInfoItem">
            <dt>수정된 날짜</dt>
            <dd>{fileInfo.lastModifyDate}</dd>
          </div>
        )}
        {fileInfo.owner && (
          <div className="viewer__docInfoItem">
            <dt>업로더</dt>
            <dd>{trimOwner(fileInfo.owner)}</dd>
          </div>
        )}
        {fileInfo.lastModifyUser && (
          <div className="viewer__docInfoItem">
            <dt>수정한 사람</dt>
            <dd>{trimOwner(fileInfo.lastModifyUser)}</dd>
          </div>
        )}
      </dl>
    </div>
  );

  // ── 렌더링: Header ────────────────────────────────────────────────────────
  const icon = getFileIconUrl(fileInfo.type, 120, staticPath);

  const renderHeader = () => (
    <div className="viewer_head">
      <div className={isImageFile ? 'title_wrap is_img' : 'title_wrap'}>
        <h1 className="title_filename ellipsis" title={fileInfo.name} style={{ paddingRight: 0 }}>
          {fileInfo.name}
        </h1>
        <img
          className="fileicon"
          src={icon}
          alt={fileInfo.type}
          onError={(e) => {
            // CDN 아이콘 로드 실패 시 숨김 처리
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
        <div className={useOpenButton ? 'title_btnarea' : 'title_btnarea v2'}>
          <button
            type="button"
            className="LS_btn Image icon_info"
            ref={fileInfoBtnRef}
            style={{ height: 22, width: 16 }}
            title="상세정보"
            onClick={() => handleAction('fileInfo')}
          >
            <span className="sp_selene" />
          </button>
        </div>
      </div>

      {renderFileInfoPopup()}

      <button type="button" className="LS_btn btn_clr" onClick={() => onClose?.()}>
        <span className="sp_selene">닫기</span>
      </button>

      <div className="header_btngroup">
        <div className="btngroup_wrap">
          {controlButtons}
        </div>
      </div>
    </div>
  );

  // ── 루트 className ────────────────────────────────────────────────────────
  let rootClassName = progressVisible || disableImg
    ? 'LS_viewer_new err_mod'
    : 'LS_viewer_new';
  rootClassName += ' type_renewal';

  const wrapClassName = 'viewer_wrap'
    + (fitWidth ? ' wide' : '');

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className={rootClassName} id="fileviewer_root">
      {/* Dimmed 배경 */}
      <div className="dimmed" id="fileviewer_dimmed" style={dimmedStyle} />

      {/* 메인 컨테이너 */}
      <div
        ref={rootRef}
        className={wrapClassName}
        style={contentStyle}
        onClick={handleClickDimmed}
      >
        {renderHeader()}
        {renderBody()}
      </div>

      {/* 로딩 스피너 */}
      {progressVisible && (
        <Spinner
          size={200}
          text="Loading..."
          style={{
            zIndex: 9999,
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: -125,
            marginLeft: -125,
          }}
        />
      )}

      {/* 에러 표시 */}
      {disableImg && (
        <div style={{
          zIndex: 1500, top: '50%', left: '50%',
          position: 'absolute', textAlign: 'center',
          transform: 'translate(-50%, -50%)',
        }}>
          <h1 style={{
            marginTop: 10, paddingBottom: 0, fontSize: 16,
            lineHeight: '22px', fontWeight: 'bold',
            fontFamily: 'Nanum Square ko, Malgun Gothic, Helvetica, Apple SD Gothic Neo, sans-serif',
            textAlign: 'center', color: '#fff',
          }}>
            {errorMsg}
          </h1>
        </div>
      )}
    </div>
  );
};

export default LUXFileViewer_R;
